import { useState, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DependencyDeleteDialog } from '@/components/ui/DependencyDeleteDialog';
import { tasksApi } from '@/services/api/tasks';
import { TaskForm } from './TaskForm';
import { StatusDropdown } from './StatusDropdown';
import { useTasks, useCreateTask, useUpdateTask, useUpdateTaskStatus, useDeleteTask } from '@/hooks/useTasks';
import { useTeamMembers } from '@/hooks';
import type { Database } from '@/types/database.types';
import type { TaskFormData } from '@/utils/validation';

type Task = Database['public']['Tables']['tasks']['Row'];

interface TaskListProps {
    /** The project ID to filter tasks by */
    projectId: number;
}

/**
 * Task list component that displays all tasks for a project
 * Shows tasks in table format with name, type, size, dates, status, and assigned member
 */
export const TaskList = ({ projectId }: TaskListProps) => {
    // Fetch tasks for the project
    const { data: tasks, isLoading, error: fetchError } = useTasks(projectId);

    // Mutations
    const createTask = useCreateTask();
    const updateTask = useUpdateTask();
    const updateStatus = useUpdateTaskStatus();
    const deleteTask = useDeleteTask();

    // Fetch team members to display names
    const { data: teamMembers } = useTeamMembers();

    // UI state
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingTask, setEditingTask] = useState<Task | undefined>(undefined);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
    const [isCheckingDependencies, setIsCheckingDependencies] = useState(false);
    const [dependentTasks, setDependentTasks] = useState<{ id: number; name: string }[]>([]);
    const [dependencyDeleteOpen, setDependencyDeleteOpen] = useState(false);

    // Create a lookup map for team member names
    const teamMemberMap = useMemo(() => {
        const map = new Map<number, string>();
        (teamMembers ?? []).forEach((member) => {
            map.set(member.id, member.name);
        });
        return map;
    }, [teamMembers]);

    // Format task type for display
    const formatTaskType = (type: Task['type']): string => {
        const typeMap: Record<Task['type'], string> = {
            design: 'Design',
            development: 'Development',
            integration: 'Integration',
            code_review: 'Code Review',
            qa: 'QA',
        };
        return typeMap[type] || type;
    };

    // Get task type color classes (per design system)
    const getTaskTypeColorClasses = (type: Task['type']): string => {
        const colorMap: Record<Task['type'], string> = {
            design: 'bg-purple-100 text-purple-800 border-purple-200',
            development: 'bg-blue-100 text-blue-800 border-blue-200',
            integration: 'bg-indigo-100 text-indigo-800 border-indigo-200',
            code_review: 'bg-cyan-100 text-cyan-800 border-cyan-200',
            qa: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        };
        return colorMap[type] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    // Get row styling based on status
    const getRowStatusClass = (status: Task['status']): string => {
        switch (status) {
            case 'completed': return 'opacity-70';
            case 'blocked': return 'bg-red-50/50';
            default: return '';
        }
    };

    // Format date for display
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    // Format size for display
    const formatSize = (sizeValue: number, sizeUnit: Task['size_unit']): string => {
        return `${sizeValue} ${sizeUnit}`;
    };

    // Handle create task click
    const handleCreateClick = useCallback(() => {
        setEditingTask(undefined);
        setIsFormOpen(true);
    }, []);

    // Handle edit task click
    const handleEditClick = useCallback((task: Task) => {
        setEditingTask(task);
        setIsFormOpen(true);
    }, []);

    // Handle delete task click
    const handleDeleteClick = useCallback(async (task: Task) => {
        // Guard against rapid/cross-task clicks while a check is in flight
        if (isCheckingDependencies) return;

        setTaskToDelete(task);
        setDeleteErrorMessage(null);
        setIsCheckingDependencies(true);

        try {
            const { data: dependents, error } = await tasksApi.getTaskDependents(task.id);

            if (error) {
                // Fall back to simple ConfirmDialog when dependency check fails
                setDependentTasks([]);
                setDeleteConfirmOpen(true);
                return;
            }

            if (!dependents || dependents.length === 0) {
                setDependentTasks([]);
                setDeleteConfirmOpen(true);
            } else {
                setDependentTasks(
                    dependents.map((d) => ({ id: d.task_id, name: d.task?.name ?? 'Unknown' }))
                );
                setDependencyDeleteOpen(true);
            }
        } finally {
            setIsCheckingDependencies(false);
        }
    }, [isCheckingDependencies]);

    // Handle form close
    const handleFormClose = useCallback(() => {
        setIsFormOpen(false);
        setEditingTask(undefined);
    }, []);

    // Handle delete dialog close
    const handleDeleteDialogClose = useCallback(() => {
        setDeleteConfirmOpen(false);
        setTaskToDelete(null);
    }, []);

    // Handle dependency delete dialog close
    const handleDependencyDeleteClose = useCallback(() => {
        setDependencyDeleteOpen(false);
        setTaskToDelete(null);
        setDependentTasks([]);
    }, []);

    // Handle delete confirmation (works for both ConfirmDialog and DependencyDeleteDialog)
    const handleDeleteConfirm = async () => {
        if (!taskToDelete) return;

        try {
            await deleteTask.mutateAsync({
                id: taskToDelete.id,
                projectId: projectId,
            });
            setSuccessMessage('Task deleted successfully!');
            setDeleteErrorMessage(null);
            setDeleteConfirmOpen(false);
            setDependencyDeleteOpen(false);
            setTaskToDelete(null);
            setDependentTasks([]);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setDeleteConfirmOpen(false);
            setDependencyDeleteOpen(false);
            setDependentTasks([]);
            setDeleteErrorMessage(
                err instanceof Error ? `Failed to delete task: ${err.message}` : 'Failed to delete task.'
            );
        }
    };

    // Handle form submission
    const handleFormSubmit = async (formData: Omit<TaskFormData, 'project_id'> & { project_id: number }) => {
        try {
            if (editingTask) {
                // Update existing task
                await updateTask.mutateAsync({
                    id: editingTask.id,
                    updates: {
                        name: formData.name,
                        type: formData.type as 'design' | 'development' | 'integration' | 'code_review' | 'qa',
                        size_unit: formData.size_unit as 'hours' | 'days',
                        size_value: typeof formData.size_value === 'string'
                            ? parseFloat(formData.size_value)
                            : formData.size_value,
                        start_date: formData.start_date,
                        end_date: formData.end_date,
                        status: formData.status as 'not_started' | 'in_progress' | 'completed' | 'blocked',
                        assigned_member_id: formData.assigned_member_id || null,
                    },
                });
                setSuccessMessage('Task updated successfully!');
            } else {
                // Create new task
                await createTask.mutateAsync({
                    project_id: formData.project_id,
                    name: formData.name,
                    type: formData.type as 'design' | 'development' | 'integration' | 'code_review' | 'qa',
                    size_unit: formData.size_unit as 'hours' | 'days',
                    size_value: typeof formData.size_value === 'string'
                        ? parseFloat(formData.size_value)
                        : formData.size_value,
                    start_date: formData.start_date,
                    end_date: formData.end_date,
                    status: formData.status as 'not_started' | 'in_progress' | 'completed' | 'blocked',
                    assigned_member_id: formData.assigned_member_id || null,
                });
                setSuccessMessage('Task created successfully!');
            }
            setIsFormOpen(false);
            setEditingTask(undefined);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            // Error is handled by React Query and passed to the form
            console.error('Form submission error:', err);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-64">
                <Spinner size="lg" />
            </div>
        );
    }

    // Error state
    if (fetchError) {
        return (
            <Alert variant="error">
                Failed to load tasks. Please try again later.
                <br />
                <span className="text-sm">{fetchError.message}</span>
            </Alert>
        );
    }

    // Empty state (per design system pattern)
    if (!tasks || tasks.length === 0) {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Tasks</h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Manage tasks for this project
                        </p>
                    </div>
                    <Button onClick={handleCreateClick} variant="primary">
                        Create Task
                    </Button>
                </div>

                {/* Delete Error Message */}
                {deleteErrorMessage && <Alert variant="error">{deleteErrorMessage}</Alert>}

                <Card>
                    <CardBody>
                        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                            {/* Icon in circular background */}
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <svg
                                    className="w-8 h-8 text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                    />
                                </svg>
                            </div>
                            {/* Title */}
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks</h3>
                            {/* Description */}
                            <p className="text-sm text-gray-600 mb-6 max-w-sm">
                                Get started by creating your first task for this project.
                            </p>
                            {/* Action Button */}
                            <Button onClick={handleCreateClick} variant="primary">
                                Create Task
                            </Button>
                        </div>
                    </CardBody>
                </Card>

                {/* Task Form Modal */}
                <TaskForm
                    isOpen={isFormOpen}
                    onClose={handleFormClose}
                    onSubmit={handleFormSubmit}
                    projectId={projectId}
                    initialData={editingTask}
                    isLoading={createTask.isPending || updateTask.isPending}
                    error={createTask.error?.message || updateTask.error?.message || null}
                    existingTasks={tasks ?? []}
                />

                {/* Delete Confirmation Dialog */}
                <ConfirmDialog
                    isOpen={deleteConfirmOpen}
                    onClose={handleDeleteDialogClose}
                    onConfirm={handleDeleteConfirm}
                    title="Delete Task"
                    message={`Are you sure you want to delete "${taskToDelete?.name}"? This action cannot be undone.`}
                    confirmText="Delete"
                    cancelText="Cancel"
                    variant="danger"
                    isLoading={deleteTask.isPending}
                />

                {/* Dependency Delete Dialog */}
                <DependencyDeleteDialog
                    isOpen={dependencyDeleteOpen}
                    onClose={handleDependencyDeleteClose}
                    onConfirm={handleDeleteConfirm}
                    taskName={taskToDelete?.name ?? ''}
                    dependentTasks={dependentTasks}
                    isLoading={deleteTask.isPending}
                />
            </div>
        );
    }

    // Table view
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Tasks</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Manage tasks for this project
                    </p>
                </div>
                <Button onClick={handleCreateClick} variant="primary">
                    Create Task
                </Button>
            </div>

            {/* Success Message */}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}

            {/* Delete Error Message */}
            {deleteErrorMessage && <Alert variant="error">{deleteErrorMessage}</Alert>}

            <Card>
                <CardHeader>
                    <h3 className="text-lg font-semibold text-gray-900">
                        All Tasks ({tasks.length})
                    </h3>
                </CardHeader>
                <CardBody>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                                    >
                                        Task Name
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                                    >
                                        Type
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                                    >
                                        Size
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                                    >
                                        Start Date
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                                    >
                                        End Date
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                                    >
                                        Status
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                                    >
                                        Assigned Member
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider"
                                    >
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {tasks.map((task) => (
                                    <tr key={task.id} className={`hover:bg-gray-50 transition-colors ${getRowStatusClass(task.status)}`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {task.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTaskTypeColorClasses(task.type)}`}
                                            >
                                                {formatTaskType(task.type)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-600">
                                                {formatSize(task.size_value, task.size_unit)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-600 font-mono">
                                                {formatDate(task.start_date)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-600 font-mono">
                                                {formatDate(task.end_date)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <StatusDropdown
                                                currentStatus={task.status}
                                                onStatusChange={(newStatus) =>
                                                    updateStatus.mutate({
                                                        taskId: task.id,
                                                        status: newStatus,
                                                        projectId,
                                                    })
                                                }
                                                isLoading={
                                                    updateStatus.isPending &&
                                                    updateStatus.variables?.taskId === task.id
                                                }
                                            />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-600">
                                                {task.assigned_member_id
                                                    ? teamMemberMap.get(task.assigned_member_id) ||
                                                    `Member #${task.assigned_member_id}`
                                                    : 'Unassigned'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    onClick={() => handleEditClick(task)}
                                                    variant="secondary"
                                                    size="sm"
                                                    aria-label={`Edit ${task.name}`}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    onClick={() => handleDeleteClick(task)}
                                                    variant="danger"
                                                    size="sm"
                                                    aria-label={`Delete ${task.name}`}
                                                    disabled={isCheckingDependencies && taskToDelete?.id === task.id}
                                                >
                                                    {isCheckingDependencies && taskToDelete?.id === task.id
                                                        ? 'Checking...'
                                                        : 'Delete'}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardBody>
            </Card>

            {/* Task Form Modal */}
            <TaskForm
                isOpen={isFormOpen}
                onClose={handleFormClose}
                onSubmit={handleFormSubmit}
                projectId={projectId}
                initialData={editingTask}
                isLoading={createTask.isPending || updateTask.isPending}
                error={createTask.error?.message || updateTask.error?.message || null}
                existingTasks={tasks ?? []}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirmOpen}
                onClose={handleDeleteDialogClose}
                onConfirm={handleDeleteConfirm}
                title="Delete Task"
                message={`Are you sure you want to delete "${taskToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                isLoading={deleteTask.isPending}
            />

            {/* Dependency Delete Dialog */}
            <DependencyDeleteDialog
                isOpen={dependencyDeleteOpen}
                onClose={handleDependencyDeleteClose}
                onConfirm={handleDeleteConfirm}
                taskName={taskToDelete?.name ?? ''}
                dependentTasks={dependentTasks}
                isLoading={deleteTask.isPending}
            />
        </div>
    );
};

TaskList.displayName = 'TaskList';

